import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { supabase } from '../../supabaseClient';
import { createClient } from '@supabase/supabase-js';
import useBackButtonGuard from '../../hooks/useBackButtonGuard';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import Sidebar from '../Sidebar';
import StaffManagement from './StaffManagement';
import Profile from '../Profile';
import OrderManagementAdmin from './OrderManagementAdmin';
import DashboardOverview from './DashboardOverview';
import CustomerInsight from './CustomerInsight';
import AIAnalytics from './AIAnalytics';
import LoyaltyRewards from './LoyaltyRewards';
import InventoryPOS from './InventoryPOS';
import MenuManagement from './MenuManagement';
import BranchManagement from './BranchManagement';
import BaristaOperations from './BaristaOperations';
import CustomerFeedback from './CustomerFeedback';
import Settings from '../Settings2';

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeMenu, setActiveMenuState] = useState(searchParams.get('section') || 'Dashboard Overview');
  const [profileChecked, setProfileChecked] = useState(false);

  const setActiveMenu = (menu) => {
    setActiveMenuState(menu);
    setSearchParams({ section: menu });
  };

  // keep state in sync when the user uses browser Back/Forward
  useEffect(() => {
    const urlMenu = searchParams.get('section');
    if (urlMenu && urlMenu !== activeMenu) setActiveMenuState(urlMenu);
  }, [searchParams]);

  useDocumentTitle(activeMenu);

  const handleBackButtonLogout = () => {
    Swal.fire({
      icon: 'warning',
      title: 'Leave Dashboard?',
      text: 'Do you want to log out and return to the home page?',
      confirmButtonText: 'Yes, Log Out',
      confirmButtonColor: '#A0826D',
      showCancelButton: true,
      cancelButtonColor: '#6B7280',
      cancelButtonText: 'Stay'
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace('/');
      }
    });
  };

  useBackButtonGuard({
    onLogout: handleBackButtonLogout
  });

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .swal2-confirm {
        background-color: #A0826D !important;
        border: none !important;
      }
      .swal2-confirm:hover {
        background-color: #8B5A3C !important;
      }
      .swal2-cancel {
        background-color: #6b7280 !important;
        border: none !important;
      }
      .swal2-cancel:hover {
        background-color: #5a5a5a !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    checkProfileCompletion();
  }, []);

  // Jump to Menu Management if a notification (price change request) sent us here
  useEffect(() => {
    const handleNavigateToMenuManagement = () => {
      setActiveMenu('Menu Management');
    };
    window.addEventListener('kaperk:navigate-menu-management', handleNavigateToMenuManagement);
    return () => window.removeEventListener('kaperk:navigate-menu-management', handleNavigateToMenuManagement);
  }, []); 

  const checkProfileCompletion = async () => {
    try {
      const userEmail = sessionStorage.getItem('userEmail');
      const userType = sessionStorage.getItem('userType');

      if (!userEmail || !userType) {
        setProfileChecked(true);
        return;
      }

      let role = '';
      let profileData = null;
      let userId = null;

      if (userType === 'admin') {
        const { data: adminData } = await supabase
          .from('admin_accounts')
          .select('*')
          .eq('email', userEmail)
          .single();

        if (!adminData) {
          setProfileChecked(true);
          return;
        }

        userId = adminData.id;
        role = 'Super Admin';

        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .eq('user_type', 'admin')
          .maybeSingle();

        profileData = profile;
      } else if (userType === 'staff') {
        const { data: staffData } = await supabase
          .from('staff')
          .select('*')
          .eq('email', userEmail)
          .single();

        if (!staffData) {
          setProfileChecked(true);
          return;
        }

        userId = staffData.id;
        role = staffData?.role || 'Staff';

        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .eq('user_type', 'staff')
          .maybeSingle();

        profileData = profile;
      }

      const missingFields = [];
      
      if (!profileData?.first_name) missingFields.push('First Name');
      if (!profileData?.last_name) missingFields.push('Last Name');
      if (!profileData?.phone_number) missingFields.push('Phone Number');
      if (!profileData?.address) missingFields.push('Address');

      if (role === 'Super Admin') {
        if (!profileData?.city) missingFields.push('City');
        if (!profileData?.state_province) missingFields.push('State/Province');
        if (!profileData?.zip_code) missingFields.push('Zip Code');
      }

      if (missingFields.length > 0) {
        setTimeout(() => {
          Swal.fire({
            icon: 'warning',
            title: 'Profile Incomplete',
            html: `
              <div style="text-align: left;">
                <p style="margin-bottom: 15px; font-weight: 500;">Please complete your profile to continue using the system.</p>
                <p style="margin-bottom: 10px; font-weight: 500;">Missing information:</p>
                <ul style="padding-left: 20px; margin-bottom: 15px; color: #DC2626;">
                  ${missingFields.map(field => `<li style="margin-bottom: 5px;">${field}</li>`).join('')}
                </ul>
                <p style="font-size: 14px; color: #666; border-top: 1px solid #E5E7EB; padding-top: 12px; margin-top: 12px;">
                  Click <strong>Complete Profile</strong> to fill in the required information now, or <strong>Remind Me Later</strong> to do it another time.
                </p>
              </div>
            `,
            confirmButtonColor: '#A0826D',
            confirmButtonText: 'Complete Profile Now',
            showCancelButton: true,
            cancelButtonColor: '#6B7280',
            cancelButtonText: 'Remind Me Later',
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then((result) => {
            if (result.isConfirmed) {
              setActiveMenu('My Profile');
            }
          });
        }, 1000); 
      }

      setProfileChecked(true);
    } catch (error) {
      setProfileChecked(true);
    }
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'Dashboard Overview':
        return (
          <DashboardOverview onGoToProfile={() => setActiveMenu('My Profile')} setActiveMenu={setActiveMenu} />
        );
      
      case 'Business Analytics':
        return <AIAnalytics onGoToProfile={() => setActiveMenu('My Profile')} />;
      
      case 'Order Management':
        return <OrderManagementAdmin onGoToProfile={() => setActiveMenu('My Profile')} />;
      
      case 'Customer Insight':
        return <CustomerInsight onGoToProfile={() => setActiveMenu('My Profile')} />;
      
      case 'Loyalty Management':
        return <LoyaltyRewards onGoToProfile={() => setActiveMenu('My Profile')} />;
      
      case 'Inventory Management':
        return <InventoryPOS onGoToProfile={() => setActiveMenu('My Profile')} />;

      case 'Menu Management':
        return <MenuManagement onGoToProfile={() => setActiveMenu('My Profile')} />;
      
      case 'Branch Management':
        return <BranchManagement onGoToProfile={() => setActiveMenu('My Profile')} />;
      
      case 'Barista Operations':
        return <BaristaOperations onGoToProfile={() => setActiveMenu('My Profile')} />;
      
      case 'Customer Feedback':
        return <CustomerFeedback onGoToProfile={() => setActiveMenu('My Profile')} />;
      
      case 'Branch Account Management':
        return <StaffManagement onGoToProfile={() => setActiveMenu('My Profile')}/>;

      case 'Settings': 
        return <Settings onGoToProfile={() => setActiveMenu('My Profile')} />;
      
      case 'My Profile':
        return <Profile onGoToProfile={() => setActiveMenu('My Profile')}/>;
      
      default:
        return (
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-800">{activeMenu}</h2>
            <p className="text-gray-600 mt-2">Content for {activeMenu} will go here</p>
          </div>
        );
    }
  };


  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
      <main className="flex-1 overflow-y-auto bg-gray-50">
        {renderContent()}
      </main>
    </div>
  );
}